import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Pagination,
  useTheme,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Tabs,
  Tab,
  Chip
} from '@mui/material';
import {
  Add,
  Campaign,
  Policy,
  Notifications,
  FilterList,
  CheckCircle,
  HourglassEmpty
} from '@mui/icons-material';
import { Layout } from '@layout/Layout';
import { useAuth } from '@/store/authStore';
import useOrganizationFeedStore from '@/store/OrganizationFeedStore';
import FeedCard from '@/components/OrganizationFeed/FeedCard';
import CreatePostModal from './CreatePostModal';
import { FeedPost, CreatePostData, UpdatePostData } from '@/services/OrganizationFeedService';

const OrganizationFeedPage: React.FC = () => {
  const theme = useTheme();
  const { user } = useAuth();
  const {
    posts,
    pagination,
    acknowledgmentData,
    loading,
    error,
    successMessage,
    currentCategory,
    fetchPosts,
    createPost,
    updatePost,
    deletePost,
    acknowledgePost,
    fetchAcknowledgments,
    setCurrentCategory,
    resetMessages
  } = useOrganizationFeedStore();

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<FeedPost | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<number | null>(null);
  const [acknowledgmentDialogOpen, setAcknowledgmentDialogOpen] = useState(false);
  const [acknowledgmentTab, setAcknowledgmentTab] = useState(0);
  const [selectedPostForAck, setSelectedPostForAck] = useState<number | null>(null);

  const canCreatePost = user?.role && ['HR', 'Admin', 'Program Manager'].includes(user.role);

  useEffect(() => {
    fetchPosts(1, 10, currentCategory || undefined);
  }, [currentCategory]);

  const handleCategoryChange = (_: React.MouseEvent<HTMLElement>, newCategory: string | null) => {
    setCurrentCategory(newCategory === '' ? null : newCategory);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    fetchPosts(page, pagination?.pageSize || 10, currentCategory || undefined);
  };

  const handleCreatePost = async (data: CreatePostData | UpdatePostData) => {
    if (editingPost) {
      return await updatePost(editingPost.id, data as UpdatePostData);
    }
    return await createPost(data as CreatePostData);
  };

  const handleEditPost = (post: FeedPost) => {
    setEditingPost(post);
    setCreateModalOpen(true);
  };

  const handleDeleteClick = (id: number) => {
    setPostToDelete(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (postToDelete) {
      await deletePost(postToDelete);
      setDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const handleAcknowledge = async (id: number) => {
    await acknowledgePost(id);
  };

  const handleViewAcknowledgments = async (id: number) => {
    setSelectedPostForAck(id);
    await fetchAcknowledgments(id);
    setAcknowledgmentDialogOpen(true);
    setAcknowledgmentTab(0);
  };

  const handleCloseCreateModal = () => {
    setCreateModalOpen(false);
    setEditingPost(null);
  };

  const handleCloseSnackbar = () => {
    resetMessages();
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
          flexWrap: 'wrap',
          gap: 2
        }}>
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 0.5
              }}
            >
              Organization Feed
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: theme.palette.text.secondary }}
            >
              Stay updated with company announcements and news
            </Typography>
          </Box>

          {canCreatePost && (
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => setCreateModalOpen(true)}
              sx={{
                px: 3,
                py: 1,
                fontWeight: 600
              }}
            >
              New Post
            </Button>
          )}
        </Box>

        {/* Filters */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 3,
          p: 2,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
          border: `1px solid ${theme.palette.divider}`
        }}>
          <FilterList sx={{ color: theme.palette.text.secondary }} />
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mr: 1 }}>
            Filter:
          </Typography>
          <ToggleButtonGroup
            value={currentCategory}
            exclusive
            onChange={handleCategoryChange}
            size="small"
          >
            <ToggleButton value="">
              All
            </ToggleButton>
            <ToggleButton value="announcement">
              <Campaign sx={{ mr: 0.5, fontSize: 18 }} />
              Announcements
            </ToggleButton>
            <ToggleButton value="policy_update">
              <Policy sx={{ mr: 0.5, fontSize: 18 }} />
              Policy Updates
            </ToggleButton>
            <ToggleButton value="general_notice">
              <Notifications sx={{ mr: 0.5, fontSize: 18 }} />
              Notices
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => resetMessages()}>
            {error}
          </Alert>
        )}

        {/* Loading State */}
        {loading && posts.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
            <CircularProgress size={50} />
          </Box>
        ) : posts.length === 0 ? (
          <Box sx={{
            textAlign: 'center',
            py: 8,
            backgroundColor: theme.palette.background.paper,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`
          }}>
            <Campaign sx={{ fontSize: 60, color: theme.palette.text.disabled, mb: 2 }} />
            <Typography variant="h6" sx={{ color: theme.palette.text.secondary, mb: 1 }}>
              No posts yet
            </Typography>
            <Typography variant="body2" sx={{ color: theme.palette.text.disabled }}>
              {canCreatePost
                ? 'Be the first to share an update with the organization!'
                : 'Check back later for updates from your organization.'}
            </Typography>
          </Box>
        ) : (
          <>
            {/* Posts List */}
            <Box sx={{ mb: 3 }}>
              {posts.map(post => (
                <FeedCard
                  key={post.id}
                  post={post}
                  currentUserId={user?.id || ''}
                  currentUserRole={user?.role || ''}
                  onAcknowledge={handleAcknowledge}
                  onEdit={handleEditPost}
                  onDelete={handleDeleteClick}
                  onViewAcknowledgments={handleViewAcknowledgments}
                />
              ))}
            </Box>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}

        {/* Create/Edit Modal */}
        <CreatePostModal
          open={createModalOpen}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreatePost}
          editingPost={editingPost}
          loading={loading}
        />

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
          <DialogTitle>Delete Post</DialogTitle>
          <DialogContent>
            <Typography>
              Are you sure you want to delete this post? This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleDeleteConfirm} color="error" variant="contained">
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* Acknowledgment Details Dialog */}
        <Dialog
          open={acknowledgmentDialogOpen}
          onClose={() => setAcknowledgmentDialogOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              Acknowledgment Details
              {acknowledgmentData && (
                <Chip
                  label={`${acknowledgmentData.acknowledged.count}/${acknowledgmentData.acknowledged.count + acknowledgmentData.unacknowledged.count}`}
                  size="small"
                  color="primary"
                />
              )}
            </Box>
          </DialogTitle>
          <DialogContent>
            <Tabs
              value={acknowledgmentTab}
              onChange={(_, newValue) => setAcknowledgmentTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab
                icon={<CheckCircle sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Acknowledged (${acknowledgmentData?.acknowledged.count || 0})`}
              />
              <Tab
                icon={<HourglassEmpty sx={{ fontSize: 18 }} />}
                iconPosition="start"
                label={`Pending (${acknowledgmentData?.unacknowledged.count || 0})`}
              />
            </Tabs>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                {acknowledgmentTab === 0 ? (
                  acknowledgmentData?.acknowledged.users.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="No acknowledgments yet"
                        sx={{ textAlign: 'center', color: theme.palette.text.secondary }}
                      />
                    </ListItem>
                  ) : (
                    acknowledgmentData?.acknowledged.users.map((ackUser) => (
                      <ListItem key={ackUser.id}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.success.main }}>
                            <CheckCircle />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={ackUser.user_name}
                          secondary={ackUser.acknowledged_at
                            ? new Date(ackUser.acknowledged_at).toLocaleString()
                            : ''
                          }
                        />
                      </ListItem>
                    ))
                  )
                ) : (
                  acknowledgmentData?.unacknowledged.users.length === 0 ? (
                    <ListItem>
                      <ListItemText
                        primary="Everyone has acknowledged!"
                        sx={{ textAlign: 'center', color: theme.palette.success.main }}
                      />
                    </ListItem>
                  ) : (
                    acknowledgmentData?.unacknowledged.users.map((pendingUser) => (
                      <ListItem key={pendingUser.id}>
                        <ListItemAvatar>
                          <Avatar sx={{ bgcolor: theme.palette.warning.main }}>
                            <HourglassEmpty />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={pendingUser.user_name}
                          secondary={pendingUser.user_email}
                        />
                      </ListItem>
                    ))
                  )
                )}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAcknowledgmentDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>

        {/* Success Snackbar */}
        <Snackbar
          open={!!successMessage}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity="success" sx={{ width: '100%' }}>
            {successMessage}
          </Alert>
        </Snackbar>
      </Container>
    </Layout>
  );
};

export default OrganizationFeedPage;
